import cn from 'classnames';
import { Appearance, Design, Size } from '@Components/designConstants';
import '@Pages/GeoMMM/ScenarioPlanning/Modals/CsvUpload/CsvUploadModal.scss';
import { useCallback, useState, ReactElement, ChangeEvent } from 'react';
import { Modal } from 'react-bootstrap';
import { MdClose, MdError, MdOutlineDownload, MdWarning } from 'react-icons/md';
import { Button } from '@Components/Button';

import { BPMFileInput } from '@Components/BPMFileInput/BPMFileInput';
import { ScenarioNode } from '@Pages/GeoMMM/ScenarioPlanning/ScenarioPlanningUtils';
import {
  CsvGranularity,
  CsvInputType,
  CsvIssue,
  ParsedUploadRow,
  generateTemplate,
  parseCsvUpload
} from '@Pages/GeoMMM/ScenarioPlanning/Modals/CsvUpload/CsvUploadUtils';
import { downloadJSONToCSV } from '../../../../../utils/download-utils';
import { conditionallyPluralizeWithCount } from '../../../../../utils/text-utils';

interface CsvUploadModalProps {
  newScenarioTree: ScenarioNode[];
  onApply: (rows: ParsedUploadRow[]) => void;
  onHide: () => void;
}

const GRANULARITY_OPTIONS: { value: CsvGranularity; label: string }[] = [
  { value: 'channel', label: 'Channel' },
  { value: 'platform', label: 'Platform' },
  { value: 'tactic', label: 'Tactic' }
];

const INPUT_TYPE_OPTIONS: { value: CsvInputType; label: string }[] = [
  { value: 'raw', label: 'Raw ($)' },
  { value: 'percent', label: '% Change' }
];

export const CsvUploadModal = ({
  newScenarioTree,
  onApply,
  onHide
}: CsvUploadModalProps): ReactElement => {
  const [granularity, setGranularity] = useState<CsvGranularity>('channel');
  const [inputType, setInputType] = useState<CsvInputType>('raw');
  const [issues, setIssues] = useState<CsvIssue[]>([]);
  const [parsedRows, setParsedRows] = useState<ParsedUploadRow[] | null>(null);

  const resetUpload = useCallback(() => {
    setIssues([]);
    setParsedRows(null);
  }, []);

  const handleDownloadTemplate = useCallback(() => {
    const rows = generateTemplate(newScenarioTree, granularity, inputType);
    downloadJSONToCSV(rows, 'scenario_spend_template');
  }, [granularity, inputType, newScenarioTree]);

  const handleFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) {
        return;
      }
      const reader = new FileReader();
      reader.onload = evt => {
        const text = evt.target?.result;
        if (typeof text !== 'string') {
          return;
        }
        const { rows, issues: newIssues } = parseCsvUpload(
          text,
          newScenarioTree,
          granularity,
          inputType
        );
        setIssues(newIssues);
        setParsedRows(rows.length > 0 ? rows : null);
      };
      reader.readAsText(file);
      e.target.value = '';
    },
    [granularity, inputType, newScenarioTree]
  );

  const hasErrors = issues.some(i => i.type === 'error');
  const isApplyEnabled = parsedRows !== null && !hasErrors;

  return (
    <Modal show onHide={onHide} centered className='csv-upload-modal'>
      <Modal.Header>
        <div className='title'>Upload Spend CSV</div>
        <button type='button' className='close-btn' onClick={onHide}>
          <MdClose />
        </button>
      </Modal.Header>
      <Modal.Body>
        <div className='config-section'>
          <div className='config-row'>
            <span className='config-label'>Granularity</span>
            <div className='radio-group'>
              {GRANULARITY_OPTIONS.map(opt => (
                <label
                  key={opt.value}
                  htmlFor={`granularity-${opt.value}`}
                  className={cn('radio-option', { selected: granularity === opt.value })}
                >
                  <input
                    id={`granularity-${opt.value}`}
                    type='radio'
                    name='granularity'
                    value={opt.value}
                    checked={granularity === opt.value}
                    onChange={() => {
                      setGranularity(opt.value);
                      resetUpload();
                    }}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>
          <div className='config-row'>
            <span className='config-label'>Input type</span>
            <div className='radio-group'>
              {INPUT_TYPE_OPTIONS.map(opt => (
                <label
                  key={opt.value}
                  htmlFor={`inputType-${opt.value}`}
                  className={cn('radio-option', { selected: inputType === opt.value })}
                >
                  <input
                    id={`inputType-${opt.value}`}
                    type='radio'
                    name='inputType'
                    value={opt.value}
                    checked={inputType === opt.value}
                    onChange={() => {
                      setInputType(opt.value);
                      resetUpload();
                    }}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className='template-row'>
          <Button
            design={Design.NEUTRAL}
            leadingElement={<MdOutlineDownload />}
            onClick={handleDownloadTemplate}
            size={Size.SM}
            appearance={Appearance.OUTLINED}
          >
            Download Template
          </Button>
        </div>
        <hr className='divider' />
        <div className='upload-section'>
          <BPMFileInput
            buttonText='Choose CSV File'
            className=''
            design={Design.NEUTRAL}
            id='csv-upload-input'
            name='csvFile'
            onChange={handleFileChange}
            size={Size.SM}
          />
          {issues.length > 0 && (
            <div className='issue-list'>
              {issues.map((issue, idx) => (
                <div key={idx} className={`issue-item issue-item--${issue.type}`}>
                  <span className='issue-icon'>
                    {issue.type === 'error' ? <MdError /> : <MdWarning />}
                  </span>
                  <span className='issue-text'>{issue.message}</span>
                </div>
              ))}
            </div>
          )}
          {isApplyEnabled && (
            <div className='success-message'>
              {conditionallyPluralizeWithCount(parsedRows.length, 'row')} ready to apply
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onHide} appearance={Appearance.OUTLINED} design={Design.NEUTRAL}>
          Cancel
        </Button>
        <Button
          disabled={!isApplyEnabled}
          onClick={() => {
            if (parsedRows) {
              onApply(parsedRows);
            }
          }}
        >
          Apply Changes
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
